import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMsal } from "@azure/msal-react";
import ConversationItem from './ConversationItem';
import { useTranslation } from '../../hooks/useTranslation';
import { authFetch } from '../../utils/auth';

const ConversationsList = ({ onCollectionUpdated, conversations: propConversations, onUpdateCollection, onConversationDeleted }) => {
  const { accounts } = useMsal();
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const userId = accounts[0]?.username;

  useEffect(() => {
    if (propConversations) {
      setConversations(propConversations);
      setIsLoading(false);
    } else {
      const fetchConversations = async () => {
        setIsLoading(true);
        
        try {
          // Add a small delay before showing loading state to prevent flashing
          const loadingTimeout = setTimeout(() => {
            setIsLoading(true);
          }, 100);
          
          // Use the API utility with the new path structure
          const response = await authFetch('/conversations');
          clearTimeout(loadingTimeout);
          
          if (response.ok) {
            const data = await response.json();
            setConversations(data);
          } else {
            console.error('Failed to fetch conversations:', response.status);
          }
        } catch (error) {
          console.error('Error fetching conversations:', error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchConversations();
    }
  }, [userId, propConversations]);

  const handleConversationDeleted = (deletedConversationId) => {
    setConversations(prevConversations => 
      prevConversations.filter(conv => conv._id !== deletedConversationId)
    );
    onConversationDeleted(deletedConversationId);
  };

  const handleUpdateConversation = (updatedConversation) => {
    setConversations(prevConversations =>
      prevConversations.map(conv => conv._id === updatedConversation._id ? updatedConversation : conv)
    );
    onUpdateCollection(updatedConversation);
  };

  // Sort conversations by latest activity
  const sortedConversations = conversations.sort((a, b) => 
    new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
  );

  const renderSkeletonLoaders = () => {
    return Array(3).fill(0).map((_, index) => (
      <div key={`skeleton-${index}`} className="p-6 border border-border-light rounded-xl mb-4 animate-pulse bg-background-card">
        <div className="h-5 bg-gray-200 rounded-lg w-3/4 mb-3"></div>
        <div className="h-4 bg-gray-200 rounded-lg w-full mb-2"></div>
        <div className="h-4 bg-gray-200 rounded-lg w-1/4"></div>
      </div>
    ));
  };

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="space-y-4 animate-fade-in">
          {renderSkeletonLoaders()}
        </div>
      ) : sortedConversations.length > 0 ? (
        <div className="space-y-3">
          {sortedConversations.map((conversation) => (
            <ConversationItem 
              key={conversation._id} 
              conversation={conversation} 
              onConversationClick={() => navigate(`/conversations/${conversation._id}`)}
              onCollectionUpdated={onCollectionUpdated}
              onUpdateCollection={handleUpdateConversation}
              onConversationDeleted={handleConversationDeleted}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 animate-fade-in">
          <div className="bg-background-card rounded-xl p-8 border border-border-light">
            <p className="text-text-detail mb-4 text-lg">{t('conversations.emptyStateMessage')}</p>
            <Link
              to="/new-conversation"
              className="text-text-primary hover:text-primary transition-colors duration-200 font-medium hover:underline"
            >
              {t('conversations.emptyStateCta')}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConversationsList;
